import React, { PropsWithChildren, use, useCallback, useMemo, useRef, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import client from "../configs/graphql.config";
import { getAuth, signInWithPhoneNumber } from "./FirebaseAuth";
import { FirebaseManagerContext, FirebaseStatus } from "./FirebaseManager";
import { GET_USER } from "../graphql/identity";
import { UPDATE_PUSH_TOKEN } from "../graphql/identity";
import Constants from "expo-constants";
import { Maybe } from "../__generated__/graphql";
import { UserFieldsFragment } from "../__generated__/operations/graphql";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/react-native";
import { AppState } from "react-native";
import {
    addSilentNotificationListener,
    removeSilentNotificationListener,
} from "../configs/notifications-listeners";
import { useSetAtom } from "jotai";
import { currentLang } from "../atoms/i18n";
import { invitationCodeStepDone } from "../atoms/auth";
import { useTracking } from "../hooks/tracking";
import { ConfirmationResult } from "@react-native-firebase/auth";
import graphqlConfig from "../configs/graphql.config";
import { useApolloErrorSnackbar } from "../hooks/snackbar";
import { useCatchLazyQueryError } from "../hooks/errors";
import { DEFAULT_LANG } from "../i18n/config";

export const IS_POST_ONBOARDING_PREPARE_DISPLAYED = "user.isPostOnboardingPrepareDisplayed";
export const IS_INITIAL_WORKER_ONBOARDING = "user.isInitialWorkerOnboarding";

export enum AuthStatus {
    Loading,
    Out,
    In,
}

interface AuthContextInterface {
    status: AuthStatus;
    user?: UserFieldsFragment | null;
    refreshUser?: () => Promise<Maybe<UserFieldsFragment> | null | undefined>;
    appExpoPushToken?: string;
    setAppExpoPushToken: (value: string) => void;
    addRefreshListener: (listener: Listener) => () => void;
    signInWithPhoneNumber: (phoneNumber: string) => Promise<ConfirmationResult>;
}

export const AuthManagerContext = React.createContext<AuthContextInterface>({
    status: AuthStatus.Loading,
    appExpoPushToken: undefined,
    setAppExpoPushToken: () => {},
    addRefreshListener: () => () => {},
    signInWithPhoneNumber: () =>
        Promise.reject(new Error("signInWithPhoneNumber called on the default AuthManagerContext")),
});

export type Listener = (user?: UserFieldsFragment | null) => Promise<void> | void;

const defaultSignInWithPhoneNumber = async (phoneNumber: string) => {
    return await signInWithPhoneNumber(
        getAuth(),
        phoneNumber,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        window?.verifier, // todo: @jimmy, do we really need this? It is very strange.
    );
};

interface UpdatePushTokenVariable {
    expoPushToken?: string;
    firebasePushToken?: string;
}

type Props = {
    appFirebasePushToken?: string;
};

const AuthManager: React.FC<PropsWithChildren<Props>> = ({ appFirebasePushToken, children }) => {
    const appState = React.useRef(AppState.currentState);
    const { status: firebaseStatus, signOut } = use(FirebaseManagerContext);
    const [appExpoPushToken, setAppExpoPushToken] = React.useState<string | undefined>();
    const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.Loading);
    const [user, setUser] = useState<UserFieldsFragment | null>();
    const setLang = useSetAtom(currentLang);
    const setInvitationCodeStepDone = useSetAtom(invitationCodeStepDone);
    const currentLoggedUserId = React.useRef<string | undefined>(undefined);
    const [getCurrentUserDetails, { error }] = useLazyQuery(GET_USER, {
        fetchPolicy: "network-only",
    });
    useApolloErrorSnackbar(error);
    const catchLazyQueryError = useCatchLazyQueryError();
    const { login, logout, trackAppOpen } = useTracking();

    const refreshListeners = useRef<Record<string, Listener>>({});

    const addRefreshListener = useCallback((listener: Listener) => {
        const id = uuidv4();
        refreshListeners.current = { ...refreshListeners.current, [id]: listener };

        return () => {
            const { [id]: _deleted, ...others } = refreshListeners.current;
            refreshListeners.current = others;
        };
    }, []);

    const refreshUser = useCallback(async () => {
        if (firebaseStatus !== FirebaseStatus.In) {
            // do not refresh user if we have no firebase user.
            // AuthManager already manage the auth status based on firebase status
            return;
        }
        //eslint-disable-next-line no-console
        console.log("[AuthManager] refreshUser");
        try {
            const currentUserDetail = await getCurrentUserDetails();
            let needSignout = false;
            if (currentUserDetail.error) {
                console.warn("Error on getCurrentUserDetails", currentUserDetail.error.message);
                // In case of non network error of if JWT has no corresponding firebase user,
                // we signout only if preserveJwtOnError is not set
                if (
                    !currentUserDetail.error.message ||
                    currentUserDetail.error.message.indexOf("auth/user-not-found") !== -1
                ) {
                    console.warn(
                        "Error on getCurrentUserDetails Sign-out ",
                        currentUserDetail.error,
                    );
                    needSignout = true;
                }
            }
            // we were logged in but now server tell us we have no more corresponding user
            // => we need to sign out
            if (currentLoggedUserId.current && currentUserDetail.data?.currentUser === null) {
                needSignout = true;
            }
            if (Constants.expoConfig?.extra?.preserveJwtOnError) {
                // in any ase we keep the user, it may be a database error or a jwt error
                needSignout = false;
            }
            if (needSignout) {
                // set loading state so that the screens will return to welcome after signout
                setAuthStatus(AuthStatus.Loading);
                void signOut();
                return null;
            } else if (currentUserDetail.data?.currentUser !== undefined) {
                const currentUser = currentUserDetail.data.currentUser;
                setUser(currentUser);

                for (const listenerId in refreshListeners.current) {
                    await refreshListeners.current[listenerId](currentUser);
                }
            }
            return currentUserDetail.data?.currentUser;
        } catch (e: unknown) {
            catchLazyQueryError(e as Error);
        }
    }, [getCurrentUserDetails, refreshListeners, firebaseStatus, signOut, catchLazyQueryError]);

    const [updatePushToken] = useMutation(UPDATE_PUSH_TOKEN);
    const onEmailVerified = React.useCallback(() => {
        void refreshUser();
    }, [refreshUser]);

    const onKycChanged = React.useCallback(() => {
        void refreshUser();
    }, [refreshUser]);

    const onClientVerify = React.useCallback(() => {
        void refreshUser();
    }, [refreshUser]);

    React.useEffect(() => {
        addSilentNotificationListener("emailVerification", onEmailVerified);
        addSilentNotificationListener("kycChanged", onKycChanged);
        addSilentNotificationListener("clientVerify", onClientVerify);
        return () => {
            removeSilentNotificationListener(onEmailVerified);
            removeSilentNotificationListener(onKycChanged);
            removeSilentNotificationListener(onClientVerify);
        };
    }, [onEmailVerified, onKycChanged, onClientVerify]);

    React.useEffect(() => {
        if (user) {
            const variables: UpdatePushTokenVariable = {};
            let needUpdate = false;
            if (appExpoPushToken && appExpoPushToken !== user.expoPushToken) {
                variables.expoPushToken = appExpoPushToken;
                needUpdate = true;
            }
            if (appFirebasePushToken && appFirebasePushToken !== user.firebasePushToken) {
                variables.firebasePushToken = appFirebasePushToken;
                needUpdate = true;
            }
            if (needUpdate) {
                void updatePushToken({
                    variables,
                });
            }
        }
    }, [user, updatePushToken, appExpoPushToken, appFirebasePushToken]);

    React.useEffect(() => {
        const subscription = AppState.addEventListener("change", (nextAppState) => {
            if (appState.current.match(/inactive|background/) && nextAppState === "active") {
                void trackAppOpen();
                // we want to be sure data are up-to-date when we come back from background
                void client.refetchQueries({
                    include: "active",
                });
                if (currentLoggedUserId.current) {
                    void refreshUser();
                }
            }
            appState.current = nextAppState;
        });

        return () => {
            subscription.remove();
        };
    }, [refreshUser, trackAppOpen]);

    React.useEffect(() => {
        if (firebaseStatus === FirebaseStatus.Loading) {
            //eslint-disable-next-line no-console
            console.log("[AuthManager] firebaseStatus === FirebaseStatus.Loading");
            setAuthStatus(AuthStatus.Loading);
        } else if (firebaseStatus === FirebaseStatus.Out) {
            //eslint-disable-next-line no-console
            console.log("[AuthManager] firebaseStatus === FirebaseStatus.Out");
            setUser(undefined);
            // the next signup (whoever makes it) asks for an invitation code again
            setInvitationCodeStepDone(false);
            setAuthStatus(AuthStatus.Out);
        } else {
            //eslint-disable-next-line no-console
            console.log("[AuthManager] firebaseStatus === FirebaseStatus.In");
            // result of getUserDetails will be managed by next useEffect :-)
            void refreshUser();
        }
    }, [firebaseStatus, refreshUser, setInvitationCodeStepDone]);

    React.useEffect(() => {
        /**
         * Logout
         */
        if (!user && currentLoggedUserId.current !== undefined) {
            //eslint-disable-next-line no-console
            console.log("[AuthManager] did logout");
            currentLoggedUserId.current = undefined;
            graphqlConfig.defaultContext.liftProfile = undefined;
            Sentry.setUser(null);
            void logout();

            setAuthStatus(AuthStatus.Out);
        } else {
            // We are logged with firebase but we don't have any corresponding user in our database
            if (user === null) {
                /**
                 * Need Signup
                 */
                //eslint-disable-next-line no-console
                console.log("[AuthManager] need signup");
                setAuthStatus(AuthStatus.Out);
            } else if (user && currentLoggedUserId.current !== user.id) {
                /**
                 * Login
                 */
                //eslint-disable-next-line no-console
                console.log("[AuthManager] did login");
                // Keep the language already resolved (device locale, or a `?lang=` deeplink)
                // when the account has no stored preference, instead of forcing the default.
                setLang((currentLangValue) => user.lang || currentLangValue || DEFAULT_LANG);
                if (user.email) {
                    Sentry.setUser({ email: user.email || "" });
                }
                currentLoggedUserId.current = user.id;
                void login(user);
                setAuthStatus(AuthStatus.In);
            }
        }
    }, [user, login, logout, setAuthStatus, setLang]);

    return (
        <AuthManagerContext.Provider
            value={{
                status: authStatus,
                user,
                refreshUser,
                appExpoPushToken,
                setAppExpoPushToken,
                addRefreshListener,
                signInWithPhoneNumber: defaultSignInWithPhoneNumber,
            }}
        >
            {children}
        </AuthManagerContext.Provider>
    );
};

type MockedAuthManagerProps = {
    user?: UserFieldsFragment | null;
    authStatus?: AuthStatus;
    refreshUser?: () => Promise<Maybe<UserFieldsFragment> | null | undefined>;
    setAppExpoPushToken?: (value: string) => void;
    addRefreshListener?: (listener: Listener) => () => void;
    signInWithPhoneNumber?: (phoneNumber: string) => Promise<ConfirmationResult>;
};

/**
 * User for Storybook and Testing
 */
export const MockedAuthManager: React.FC<PropsWithChildren<MockedAuthManagerProps>> = ({
    children,
    user,
    authStatus,
    refreshUser = () => Promise.resolve(user),
    setAppExpoPushToken = () => {},
    addRefreshListener = () => () => {},
    signInWithPhoneNumber: propsSignInWithPhoneNumber,
}) => {
    authStatus = authStatus ?? (user ? AuthStatus.In : AuthStatus.Out);

    const signInWithPhoneNumber = useMemo(
        () => propsSignInWithPhoneNumber || defaultSignInWithPhoneNumber,
        [propsSignInWithPhoneNumber],
    );

    return (
        <AuthManagerContext.Provider
            value={{
                status: authStatus,
                user,
                refreshUser,
                setAppExpoPushToken,
                addRefreshListener,
                signInWithPhoneNumber,
            }}
        >
            {children}
        </AuthManagerContext.Provider>
    );
};
export default AuthManager;
